import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { taskAPI, shiftTemplateAPI, employeeAPI } from '../../services';

/**
 * #015517 — POPUP CHỈ XEM thông tin 1 task (Cấu hình Task), mở được bằng LINK riêng.
 *
 * Luật gốc (lệnh sếp update_id 731601333): mỗi task phải có 1 địa chỉ web riêng
 * `admin.on-coffee.com/tasks?id=<id>` để làm BẰNG CHỨNG mỗi lần sếp bảo "thêm task"; mở link là
 * hiện ngay popup của đúng task đó. Popup KHÔNG có ô nhập — muốn sửa thì bấm nút "Sửa" để mở lại
 * đúng màn sửa đang có (TaskFormModal), không dựng màn sửa thứ hai.
 *
 * Deep-link + đăng nhập: `RequireAuth` đã giữ `?next=<path+query>` khi chưa đăng nhập
 * (xem `utils/nextParam.js`) nên link này cũng quay lại ĐÚNG popup sau khi đăng nhập.
 */

/** #142526 — nhãn tiếng Việt của khung giờ trong ca (đầu/giữa/cuối ca). */
const SHIFT_SEGMENT_LABEL = {
  START: 'Đầu ca',
  MIDDLE: 'Giữa ca',
  END: 'Cuối ca',
};

const DOW_LABELS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

const isVideoUrl = (url) =>
  /\.(mp4|webm|mov|avi|mkv|ogv)(\?|$)/i.test(url || '') || (url || '').includes('/video/upload/');

/** Định dạng ngày-giờ theo giờ Việt Nam cho dễ đọc (không phụ thuộc locale máy người xem). */
const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Địa chỉ web RIÊNG của 1 task. Dùng `window.location.origin` để tự đúng cả trên bản thật
 * (admin.on-coffee.com) lẫn máy dev — KHÔNG ghim cứng domain (khuôn link-sâu mục 2).
 */
export const buildTaskDetailLink = (taskId) => {
  if (!taskId || typeof window === 'undefined') return '';
  return `${window.location.origin}/tasks?id=${encodeURIComponent(taskId)}`;
};

/** Copy vào clipboard, có đường lùi cho trình duyệt không cho dùng `navigator.clipboard` (http). */
const copyText = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // rơi xuống đường lùi bên dưới
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch (err) {
    return false;
  }
};

/** Một dòng "nhãn — giá trị" cho gọn, tự xuống dòng trên điện thoại. */
const InfoRow = ({ label, children }) => (
  <div className="d-flex flex-column flex-sm-row gap-1 mb-2">
    <div className="text-muted small flex-shrink-0" style={{ minWidth: 150 }}>
      {label}
    </div>
    <div className="flex-grow-1" style={{ wordBreak: 'break-word' }}>
      {children}
    </div>
  </div>
);

export default function TaskDetailModal({
  show,
  taskId,
  branches = [],
  canEdit = false,
  refreshKey = 0,
  onHide,
  onEdit,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [task, setTask] = useState(null);
  const [links, setLinks] = useState([]);
  const [creatorName, setCreatorName] = useState('');
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!show || !taskId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTask(null);
    setLinks([]);
    setCreatorName('');
    setCopied(false);

    (async () => {
      try {
        const [detail, linkRes] = await Promise.all([
          taskAPI.getTaskById(taskId),
          shiftTemplateAPI.getTaskShiftLinks({ task_ids: [taskId] }).catch(() => null),
        ]);
        if (cancelled) return;
        const t = detail?.data || detail;
        if (!t || !t.id) {
          setError('Không tìm thấy task này (có thể đã bị xoá).');
          return;
        }
        setTask(t);
        const list = linkRes?.data?.[taskId] || [];
        setLinks(Array.isArray(list) ? list : []);

        if (t.created_by) {
          try {
            const emp = await employeeAPI.getEmployeeById(t.created_by);
            if (!cancelled) setCreatorName((emp?.data || emp)?.full_name || '');
          } catch (err) {
            // Không đọc được tên người tạo thì vẫn hiện phần còn lại, không chặn popup.
          }
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Không tải được thông tin task');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, taskId, refreshKey]);

  const samples = useMemo(() => {
    if (!task) return [];
    const cfg = Array.isArray(task.sample_evidence_config) ? task.sample_evidence_config : [];
    if (cfg.length > 0) return cfg;
    return (task.sample_evidence_urls || []).map((url) => ({ url, criteria: [] }));
  }, [task]);

  const checklist = useMemo(() => {
    if (!task || !Array.isArray(task.checklist_items)) return [];
    return task.checklist_items.filter((x) => typeof x === 'string' && x.trim() !== '');
  }, [task]);

  const branchName = useMemo(() => {
    if (!task) return '';
    if (!task.branch_id) return 'Tất cả chi nhánh';
    return branches.find((b) => b.id === task.branch_id)?.name || task.branch_id;
  }, [task, branches]);

  const taskLink = task?.id ? buildTaskDetailLink(task.id) : '';

  const handleCopyLink = async () => {
    const ok = await copyText(taskLink);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2500);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" scrollable fullscreen="sm-down" centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5" className="text-break" data-testid="td-title">
            {task?.name || 'Chi tiết task'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading && !task && (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Đang tải...</span>
              </Spinner>
            </div>
          )}

          {error && (
            <Alert variant="warning" data-testid="td-error">
              {error}
            </Alert>
          )}

          {task && (
            <div data-testid="td-content">
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Badge bg={task.is_active ? 'success' : 'secondary'} data-testid="td-status">
                  {task.is_active ? 'Đang hoạt động' : 'Tạm dừng'}
                </Badge>
                {task.task_type === 'MAIN_TASK' ? (
                  <Badge bg="primary">Việc trực ca</Badge>
                ) : (
                  <Badge bg="secondary">Việc phải nghiệm thu</Badge>
                )}
                {task.dual_review && (
                  <Badge bg="warning" text="dark">
                    Nghiệm thu 2 lần (Đầu + Cuối ca)
                  </Badge>
                )}
                {task.is_urgent && <Badge bg="danger">Khẩn cấp</Badge>}
                <Badge bg="light" text="dark" className="border">
                  {task.category}
                </Badge>
              </div>

              {task.work_content && (
                <div className="mb-3">
                  <div className="fw-semibold mb-1">Nội dung công việc</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{task.work_content}</div>
                </div>
              )}

              {task.description && (
                <div className="mb-3">
                  <div className="fw-semibold mb-1">Mô tả</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{task.description}</div>
                </div>
              )}

              {checklist.length > 0 && (
                <div className="mb-3">
                  <div className="fw-semibold mb-1">Tiêu chí / Checklist hoàn thành</div>
                  <ul className="mb-0 ps-3">
                    {checklist.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {samples.length > 0 && (
                <div className="mb-3">
                  <div className="fw-semibold mb-2">Ảnh/video mẫu &amp; tiêu chí QC</div>
                  <div className="d-flex flex-wrap gap-3">
                    {samples.map((s, idx) => (
                      <div key={idx} style={{ width: 170 }}>
                        <button
                          type="button"
                          className="p-0 border rounded bg-transparent"
                          style={{ cursor: 'zoom-in', lineHeight: 0 }}
                          onClick={() => setPreview({ url: s.url, isVideo: isVideoUrl(s.url) })}
                          data-testid={`td-sample-${idx}`}
                          aria-label={`Xem to mẫu ${idx + 1}`}
                        >
                          {isVideoUrl(s.url) ? (
                            <video
                              src={s.url}
                              style={{ width: 170, height: 170, objectFit: 'cover', borderRadius: 4 }}
                              muted
                              preload="metadata"
                            />
                          ) : (
                            <img
                              src={s.url}
                              alt={`sample-${idx}`}
                              style={{ width: 170, height: 170, objectFit: 'cover', borderRadius: 4 }}
                            />
                          )}
                        </button>
                        <div className="small fw-bold mt-1">Mẫu {idx + 1}</div>
                        {(s.criteria || []).length > 0 ? (
                          <ul className="mb-0 ps-3 small">
                            {s.criteria.map((c, ci) => (
                              <li key={ci}>{c?.name}</li>
                            ))}
                          </ul>
                        ) : (
                          <div className="small text-muted">Không có tiêu chí</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <hr />

              <InfoRow label="Chi nhánh">{branchName}</InfoRow>
              <InfoRow label="Khung giờ trong ca">
                {task.dual_review ? (
                  <span>Đầu ca + Cuối ca</span>
                ) : task.shift_segment ? (
                  <Badge bg="info">{SHIFT_SEGMENT_LABEL[task.shift_segment] || task.shift_segment}</Badge>
                ) : (
                  <span className="text-muted">Không ràng buộc</span>
                )}
              </InfoRow>
              <InfoRow label="Ca áp dụng">
                {links.length === 0 ? (
                  <span className="text-muted">Chưa gắn ca nào</span>
                ) : (
                  <div className="d-flex flex-wrap gap-1">
                    {links.map((l) => (
                      <Badge key={l.id} bg={l.template_is_active === false ? 'secondary' : 'primary'}>
                        {l.template_name}
                        {l.branch_name ? ` · ${l.branch_name}` : ''}
                      </Badge>
                    ))}
                  </div>
                )}
              </InfoRow>
              <InfoRow label="Ngày áp dụng">
                {(task.days_of_week || []).length === 0 ? (
                  <span className="text-muted">Mọi ngày</span>
                ) : (
                  <div className="d-flex flex-wrap gap-1">
                    {task.days_of_week.map((d) => (
                      <Badge key={d} bg="info">
                        {DOW_LABELS[d] ?? d}
                      </Badge>
                    ))}
                  </div>
                )}
              </InfoRow>
              <InfoRow label="Hạn hoàn thành">
                {task.deadline_minutes_after_shift_start != null
                  ? `Trong ${task.deadline_minutes_after_shift_start} phút đầu ca`
                  : 'Không đặt hạn'}
              </InfoRow>
              <InfoRow label="Thời lượng ước tính">
                {task.estimated_minutes != null ? `${task.estimated_minutes} phút` : '—'}
              </InfoRow>
              <InfoRow label="Điểm">
                {task.has_points ? (
                  <Badge bg={(task.points ?? 0) >= 0 ? 'success' : 'danger'}>
                    {(task.points ?? 0) >= 0 ? '+' : ''}
                    {task.points ?? 0}
                  </Badge>
                ) : (
                  <span className="text-muted">Không tính điểm</span>
                )}
              </InfoRow>
              <InfoRow label="Bắt buộc bằng chứng">
                {task.requires_evidence ? 'Có' : 'Không'}
              </InfoRow>
              <InfoRow label="Bắt buộc mới check-out">
                {task.required_for_checkout ? 'Có' : 'Không'}
              </InfoRow>
              <InfoRow label="Người tạo">
                {creatorName || task.created_by || '—'}
              </InfoRow>
              <InfoRow label="Lúc tạo">{formatDateTime(task.created_at)}</InfoRow>
              {task.updated_at && task.updated_at !== task.created_at && (
                <InfoRow label="Cập nhật cuối">{formatDateTime(task.updated_at)}</InfoRow>
              )}

              <div className="mt-3">
                <div className="fw-semibold mb-1">Link chia sẻ task này</div>
                <div className="d-flex gap-2">
                  <input
                    className="form-control"
                    style={{ fontSize: 16 }}
                    value={taskLink}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    data-testid="td-link"
                  />
                  <Button
                    variant={copied ? 'success' : 'outline-primary'}
                    className="flex-shrink-0"
                    onClick={handleCopyLink}
                    data-testid="td-copy-link"
                  >
                    {copied ? 'Đã chép' : 'Sao chép'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="flex-nowrap">
          <Button
            variant="secondary"
            className="flex-fill"
            style={{ fontSize: 16 }}
            onClick={onHide}
            data-testid="td-close"
          >
            Đóng
          </Button>
          {canEdit && task && (
            <Button
              variant="primary"
              className="flex-fill"
              style={{ fontSize: 16 }}
              onClick={() => onEdit?.(task)}
              data-testid="td-edit"
            >
              Sửa
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Xem ảnh/video mẫu ở khổ to — chỉ hiển thị, không có ô nhập. */}
      <Modal show={!!preview} onHide={() => setPreview(null)} size="xl" centered fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title as="h6">Ảnh/video mẫu</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0">
          {preview?.isVideo ? (
            <video src={preview.url} controls style={{ maxWidth: '100%', maxHeight: '80vh' }} />
          ) : (
            <img src={preview?.url} alt="Mẫu" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
